import React, { useMemo, useState } from 'react'
import { View } from 'react-native'
import { useNavigation } from '@react-navigation/native'
import { Trans } from 'react-i18next'
import moment from 'moment/moment'
import type { Moment } from 'moment'
import MaterialIcon from 'react-native-vector-icons/MaterialIcons'
import MaterialCommunityIcon from 'react-native-vector-icons/MaterialCommunityIcons'

import { useSelector, useDispatch } from 'APP/Hooks/redux'
import type { NormalizedAppointment } from 'APP/Store/History/types'
import { selectProvider } from 'APP/Store/ProviderBooking/selectors'
import PatientHistoryActions from 'APP/Redux/PatientHistoryRedux'
import BottomSheetModal from 'APP/Components/BottomSheetModal'
import DialogueTouchableOpacity from 'APP/Components/DialogueTouchableOpacity'
import Button from 'APP/Converse/Button'
import IconCard from 'APP/Converse/IconCard'
import Typography from 'APP/Converse/Typography'

import { useAddToCalendar } from 'APP/Hooks/useAddToCalendar'
import Analytics from 'APP/Services/Analytics'
import I18n, { isTranslationKey } from 'APP/I18n'
import {
  getImage,
  getPractitionerRoleLabel,
  patchPractitionerProfileData,
} from 'APP/Lib/PractitionerHelpers'
import {
  getAppointmentManagementCopy,
  AppointmentManagementCopyConfig,
  APPOINTMENT_ACTIONS,
} from 'APP/Lib/AppointmentManagementHelpers'
// TODO: remove using Colors from App/Themes in this file in the next PR
import styled, { useTheme } from 'styled-components/native'

const StyledWrapper = styled(View)`
  margin-vertical: ${({ theme }) => theme.metrics.baseMargin / 3}px;
  display: flex;
  border-radius: ${({ theme }) => theme.metrics.baseMargin}px;
  background-color: ${({ theme }) => theme.colors.elementsBg};
  ${({ theme }) => theme.shadows.normal};
`

const StyledDialogueTouchableOpacity = styled(DialogueTouchableOpacity)`
  margin-vertical: ${({ theme }) => theme.metrics.baseMargin / 3}px;
  display: flex;
  border-radius: ${({ theme }) => theme.metrics.baseMargin}px;
  background-color: ${({ theme }) => theme.colors.elementsBg};
  ${({ theme }) => theme.shadows.normal};
  margin-horizontal: ${({ theme }) => theme.metrics.baseMargin}px;
`

const AppointmentContainer = styled(View).attrs(
  ({ accessibilityRole }: { accessibilityRole?: string }) => ({
    accessibilityRole: accessibilityRole,
  })
)`
  flex-direction: row;
`

const DateContainer = styled.View`
  justify-content: center;
  align-items: center;
  background-color: ${({ theme }) => theme.colors.appointmentDateElement};
  border-top-left-radius: ${({ theme }) => theme.metrics.baseMargin}px;
  border-bottom-left-radius: ${({ theme }) => theme.metrics.baseMargin}px;
  padding: ${({ theme }) => theme.metrics.baseMargin}px;
`

const AppointmentDetailContainer = styled.View`
  flex: 1;
  margin: ${({ theme }) => theme.metrics.baseMargin}px;
`

const TimeContainer = styled.View`
  flex-direction: row;
  align-content: center;
  align-items: center;
  margin-bottom: ${({ theme }) => theme.metrics.baseMargin / 4}px;
`

const StyledTime = styled(Typography)`
  margin-left: ${({ theme }) => theme.metrics.baseMargin / 2}px;
`

const LocationContainer = styled.View`
  flex-direction: row;
  align-items: center;
  margin-bottom: ${({ theme }) => theme.metrics.baseMargin / 1.5}px;
`

const LocationCopy = styled(Typography)`
  margin-left: ${({ theme }) => theme.metrics.baseMargin / 2}px;
`

const ChildLabel = styled(Typography)`
  background-color: ${({ theme }) => theme.colors.tagBg};
  border-radius: 10;
  overflow: hidden;
  padding-vertical: ${({ theme }) => theme.metrics.baseMargin / 4}px;
  padding-horizontal: ${({ theme }) => theme.metrics.baseMargin / 2}px;
  margin-left: ${({ theme }) => theme.metrics.baseMargin / 2}px;
`

const PractitionerContainer = styled.View`
  flex-direction: row;
`

const PractitionerInnerContainer = styled.View`
  flex: 1;
  margin-left: ${({ theme }) => theme.metrics.baseMargin}px;
  align-self: center;
  gap: ${({ theme }) => theme.metrics.baseMargin / 4}px;
`

const ReasonContainer = styled.View`
  margin-top: ${({ theme }) => theme.metrics.baseMargin / 4}px;
`

const QuickActionsWrapper = styled.View`
  flex-direction: row;
  justify-content: space-between;
  gap: ${({ theme }) => theme.metrics.baseMargin}px;
  margin-top: ${({ theme }) => theme.metrics.baseMargin}px;
`

const ButtonContainer = styled.View`
  margin-top: ${({ theme }) => theme.metrics.baseMargin * 1.5}px;
  margin-bottom: ${({ theme }) => theme.metrics.baseMargin / 2}px;
`

const StyledButton = styled(Button)`
  margin-bottom: ${({ theme }) => theme.metrics.baseMargin}px;
`

const StyledBottomSheetModal = styled(BottomSheetModal)`
  align-items: center;
  padding-bottom: ${({ theme }) => theme.insets.bottom}px;
`

// TODO: move 550px to theme metrics and update all instances
const BottomModalContainer = styled.View`
  padding-horizontal: ${({ theme }) => theme.metrics.baseMargin * 2}px;
  padding-bottom: ${({ theme }) => theme.metrics.baseMargin}px;
  align-items: center;
  max-width: 550px;
  gap: ${({ theme }) => theme.metrics.baseMargin}px;
`

const FooterNote = styled(Typography)`
  margin-bottom: ${({ theme }) => theme.metrics.baseMargin}px;
  align-self: stretch;
`

type TimeProps = {
  startTime: Moment
  endTime: Moment
  child: NormalizedAppointment['child']
}

type LocationProps = {
  location: string
}

type ConfirmationSheetConfig = AppointmentManagementCopyConfig & {
  isLoading: boolean
  onConfirm: () => void
  analyticsName: string
  testID: string
  closeAnalyticsValue: string
}

type ConfirmationBottomSheetProps = {
  isVisible: boolean
  onClose: () => void
  config: ConfirmationSheetConfig | null
}

type ManageButtonsProps = {
  appointment: NormalizedAppointment
  onCancelAppointment: () => void
  onRescheduleAppointment: () => void
  isCancelRequestRunning: boolean
  isRescheduleRequestRunning: boolean
}

export type AppointmentCardProps = {
  style?: object
  appointment: NormalizedAppointment
  disabled?: boolean
  showButtons?: boolean
  showQuickActions?: boolean
}

const Time = ({ startTime, endTime, child }: TimeProps) => {
  const theme = useTheme()
  return (
    <TimeContainer>
      <MaterialCommunityIcon
        name="clock-outline"
        size={14}
        color={theme.colors.accent}
        accessibilityRole="image"
      />
      <StyledTime
        variant="bodySmallBold"
        accessibilityRole="text"
        accessibilityLabel={`${startTime.format('h[:]mm')} - ${endTime.format('h[:]mm A z')}`}
      >
        {/* This component is Typography, which is Text. So it's not raw text. */}
        {/* eslint-disable-next-line react-native/no-raw-text */}
        {startTime.format('h[:]mm')} - {endTime.format('h[:]mm A z')}
      </StyledTime>
      {child && (
        // @ts-expect-error FIXME: child.givenName is not guaranteed to be set
        <ChildLabel variant="caption" accessibilityRole="text" accessibilityLabel={child.givenName}>
          {child.givenName}
        </ChildLabel>
      )}
    </TimeContainer>
  )
}

const Location = ({ location }: LocationProps) => {
  const theme = useTheme()

  const isVirtual = location === 'virtual'

  if (isVirtual) {
    return null
  }

  const locationKey = `Appointments.location.${location}`
  const locationName = isTranslationKey(locationKey) ? I18n.t(locationKey) : undefined
  const inPerson = I18n.t('AppointmentBooking.in_person')

  return (
    <LocationContainer>
      <MaterialIcon
        name={'apartment'}
        size={14}
        color={theme.colors.accent}
        accessibilityRole="image"
      />
      <LocationCopy
        testID="LOCATION_COPY"
        variant="bodySmallBold"
        accessibilityRole="text"
        accessibilityLabel={locationName}
      >
        {locationName ? `${inPerson} (${locationName})` : inPerson}
      </LocationCopy>
    </LocationContainer>
  )
}

// FIXME: convert patchPractitionerProfileData to Typescript and colocate its response model next to it
const Practitioner = ({
  practitioner,
}: {
  practitioner: NormalizedAppointment['practitioner'] & { full_name: string }
}) => {
  const navigation = useNavigation()
  const profile = useSelector((state) => state.patient.profile)
  // @ts-expect-error FIXME: handle falsy practitioner ID
  const provider = useSelector((state) => selectProvider(state, practitioner.id))
  const avatarStyles = {
    width: 40,
    height: 40,
    resizeMode: 'contain',
    borderRadius: 40,
  }

  const handlePractitionerProfilePress = (id: number) => {
    if (!id) return
    // @ts-expect-error TODO: add route & args to custom.d.ts
    navigation.navigate('practitionerProfile', { userAppId: id, profileId: profile.id })
  }

  const providerRole = getPractitionerRoleLabel(provider?.licenses, practitioner?.job_title)

  return (
    <PractitionerContainer>
      <DialogueTouchableOpacity
        analyticsName={'upcomingAppointmentCard.practitionerBio'}
        // @ts-expect-error FIXME: handle falsy practitioner ID
        onPress={() => handlePractitionerProfilePress(practitioner.id)}
        testID={'PRACTITIONER_AVATAR'}
      >
        {getImage(practitioner, avatarStyles)}
      </DialogueTouchableOpacity>
      <PractitionerInnerContainer>
        <Typography variant="h4">{practitioner.full_name}</Typography>
        {providerRole && <Typography variant="bodySmall">{providerRole}</Typography>}
      </PractitionerInnerContainer>
    </PractitionerContainer>
  )
}

const ReasonForConsult = ({ issueTypeId }: { issueTypeId: string }) => {
  // Fallback to nothing if no issue type and don't render
  const issue = I18n.t(`health-issue-types:${issueTypeId}`, '')
  return (
    issue.length > 0 && (
      <ReasonContainer>
        <Typography variant="bodyNormalBold">
          {I18n.t('Appointments.reasonForConsult')}
          <Typography variant="bodyNormal">{issue}</Typography>
        </Typography>
      </ReasonContainer>
    )
  )
}

const ConfirmationBottomSheet = ({ isVisible, onClose, config }: ConfirmationBottomSheetProps) => {
  if (!config) return null

  const theme = useTheme()
  // FIXME: Refactor. All of this.

  const {
    title,
    subtitle,
    noticeTitle,
    noticeBody,
    confirmLabel,
    isLoading,
    onConfirm,
    analyticsName = 'Confirmation action',
    testID = 'CONFIRMATION_BOTTOM_SHEET',
    closeAnalyticsValue,
    footerNote,
    i18nKey,
    i18nOptions,
  } = config

  const handleConfirm = () => {
    onConfirm()
    onClose()
  }

  const handleClose = () => {
    Analytics.trackEvent('button_click', {
      button_value: closeAnalyticsValue,
    })

    onClose()
  }

  // FIXME: Why are we passing the text, to then get the text again via i18n key

  return (
    <StyledBottomSheetModal
      isVisible={isVisible}
      title={title}
      subtitle={subtitle}
      onClose={handleClose}
      testID={testID}
    >
      <BottomModalContainer>
        <IconCard
          title={noticeTitle}
          description={
            <>
              {noticeBody.map((desc, index) => (
                <Typography key={`appt-card-desc-${index}`} variant="bodyNormal">
                  {i18nKey && i18nOptions ? (
                    <Trans
                      i18n={I18n}
                      defaults={desc}
                      components={[<Typography variant="bodyNormalBold" key="bold" />]}
                    />
                  ) : (
                    desc
                  )}
                </Typography>
              ))}
            </>
          }
          leftIcon={{
            src: 'info-outline',
            color: 'accent',
          }}
          style={{
            backgroundColor: theme.colors.selected,
          }}
          testID="APPOINTMENT_CARD_NOTICE"
        />
        {footerNote && (
          <FooterNote variant="bodySmall" accessibilityRole="text">
            {i18nKey && i18nOptions ? (
              <Trans
                i18n={I18n}
                defaults={footerNote}
                components={[<Typography variant="bodySmallBold" key="bold" />]}
              />
            ) : (
              footerNote
            )}
          </FooterNote>
        )}
        <Button
          onPress={handleConfirm}
          variant="tertiaryAlert"
          title={confirmLabel}
          analyticsName={analyticsName}
          widthVariant="full"
          testID={`${testID}_CONFIRM_BTN`}
          textColor="error"
          isLoading={isLoading}
        />
      </BottomModalContainer>
    </StyledBottomSheetModal>
  )
}

const QuickActions = ({ appointment }: { appointment: NormalizedAppointment }) => {
  const navigation = useNavigation()

  const handleManagePress = () => {
    navigation.navigate('upcomingAppointment', { appointment })
  }

  const handleAccessPress = () => {
    // @ts-expect-error TODO: consider conditionally rendering the access button if
    // appointment.episode_id is falsy, since we cannot navigate to a `conversation`
    // screen without a channelId
    navigation.navigate('conversation', { channelId: appointment.episode_id })
  }

  return (
    <QuickActionsWrapper>
      <Button
        small
        variant="secondary"
        widthVariant="flex"
        title={I18n.t('Appointments.upcomingCard.manage')}
        analyticsName="upcomingAppointmentCard.manage"
        onPress={handleManagePress}
        testID="UPCOMING_APPT_CARD_MANAGE_BTN"
      />
      <Button
        small
        variant="primary"
        widthVariant="flex"
        title={I18n.t('Appointments.upcomingCard.access')}
        analyticsName="UpcomingAppointmentCard.access"
        onPress={handleAccessPress}
        testID="UPCOMING_APPT_CARD_ACCESS_BTN"
      />
    </QuickActionsWrapper>
  )
}

const ManageButtons = ({
  appointment,
  onCancelAppointment,
  onRescheduleAppointment,
  isCancelRequestRunning,
  isRescheduleRequestRunning,
}: ManageButtonsProps) => {
  const isVirtualAppointment = appointment?.encounter_location === 'virtual'
  const isAppointmentModifiable = moment().isBefore(appointment.start_at)

  const handleAddToCalPress = useAddToCalendar({
    startAt: appointment.start_at,
    endAt: appointment.end_at,
    episodeId: appointment.episode_id,
    isVirtual: isVirtualAppointment,
    analyticsName: 'upcomingAppointmentCard.addToCalendar',
  })

  return (
    <ButtonContainer>
      <StyledButton
        variant="primary"
        widthVariant="full"
        title={I18n.t('Appointments.addToCalendar')}
        analyticsName="upcomingAppointmentCard.addToCalendar"
        onPress={handleAddToCalPress}
        testID="ADD_APPT_BTN"
      />
      {appointment.episode_id && (
        <>
          {isAppointmentModifiable && (
            <>
              {isVirtualAppointment && (
                <StyledButton
                  variant="secondary"
                  widthVariant="full"
                  title={I18n.t('Appointments.reschedule')}
                  analyticsName="upcomingAppointmentCard.reschedule"
                  onPress={onRescheduleAppointment}
                  showActivityIndicator={isRescheduleRequestRunning}
                  testID="RESCHEDULE_APPT_BTN"
                />
              )}
              <Button
                variant="tertiaryAlert"
                widthVariant="full"
                textColor="error"
                title={I18n.t('Appointments.cancel')}
                analyticsName="upcomingAppointmentCard.cancel"
                onPress={onCancelAppointment}
                isLoading={isCancelRequestRunning}
                testID="CANCEL_APPT_BTN"
              />
            </>
          )}
        </>
      )}
    </ButtonContainer>
  )
}

// TODO add support for past appointments DIA-48643
const AppointmentCard = ({
  appointment,
  disabled = false,
  showButtons = false,
  showQuickActions = false,
}: AppointmentCardProps) => {
  const navigation = useNavigation()
  const dispatch = useDispatch()

  const isCancelRequestRunning = useSelector((state) => state.history.cancelAppointmentRunning)
  const isRescheduleRequestRunning = useSelector(
    (state) => state.history.rescheduleAppointmentRunning
  )

  // @ts-expect-error FIXME: episode_id may be null, and null indexing is invalid
  const episode = useSelector((state) => state.history.episodes[appointment.episode_id])
  const practitioners = useSelector((state) => state.history.practitioners)
  const eligibleServices = useSelector((state) => state.patient.profile?.eligibleServices)
  const paymentInfo = useSelector((state) => state.patient.profile?.creditCard)

  const practitioner = useMemo(() => {
    return patchPractitionerProfileData(appointment?.practitioner, practitioners)
  }, [appointment.practitioner, practitioners])

  const startTime = moment(appointment.start_at)
  const endTime = moment.tz(appointment.end_at, moment.tz.guess(true))

  const [sheetConfig, setSheetConfig] = useState<ConfirmationSheetConfig | null>(null)

  const onCancelAppointment = () => {
    const copy = getAppointmentManagementCopy(
      appointment,
      APPOINTMENT_ACTIONS.CANCEL,
      eligibleServices,
      paymentInfo
    )

    setSheetConfig({
      title: copy.title,
      subtitle: copy.subtitle,
      noticeTitle: copy.noticeTitle,
      noticeBody: copy.noticeBody,
      confirmLabel: copy.confirmLabel,
      isLoading: isCancelRequestRunning,
      onConfirm: () => {
        dispatch(PatientHistoryActions.cancelAppointmentRequest(appointment))
      },
      analyticsName: 'Yes, cancel appointment',
      testID: 'CANCEL_APPT_BOTTOM_SHEET',
      closeAnalyticsValue: 'Close cancel appointment',
      footerNote: copy.footerNote,
      i18nKey: copy.i18nKey,
      i18nOptions: copy.i18nOptions,
    })
  }

  const onRescheduleAppointment = () => {
    const copy = getAppointmentManagementCopy(
      appointment,
      APPOINTMENT_ACTIONS.RESCHEDULE,
      eligibleServices,
      paymentInfo
    )

    setSheetConfig({
      title: copy.title,
      subtitle: copy.subtitle,
      noticeTitle: copy.noticeTitle,
      noticeBody: copy.noticeBody,
      confirmLabel: copy.confirmLabel,
      isLoading: isRescheduleRequestRunning,
      onConfirm: () => {
        dispatch(PatientHistoryActions.rescheduleAppointmentRequest(appointment))
      },
      analyticsName: 'Yes, reschedule appointment',
      testID: 'RESCHEDULE_APPT_BOTTOM_SHEET',
      closeAnalyticsValue: 'Close reschedule appointment',
      footerNote: copy.footerNote,
      i18nKey: copy.i18nKey,
      i18nOptions: copy.i18nOptions,
    })
  }

  const WrapperComponent = disabled ? StyledWrapper : StyledDialogueTouchableOpacity

  const containerAccessibilityRole = disabled ? undefined : 'button'

  return (
    <>
      <WrapperComponent
        onPress={
          disabled ? undefined : () => navigation.navigate('upcomingAppointment', { appointment })
        }
        analyticsName={'upcomingAppointmentCard'}
        testID={'UPCOMING_APPOINTMENT_CARD'}
      >
        <AppointmentContainer accessibilityRole={containerAccessibilityRole}>
          <DateContainer>
            <Typography
              variant="h1"
              color="darkText"
              align="center"
              accessibilityRole="text"
              accessibilityLabel={startTime.format('D')}
              testID="APPT_DATE_DAY"
            >
              {startTime.format('D')}
            </Typography>
            <Typography
              variant="bodyLarge"
              color="darkText"
              align="center"
              accessibilityRole="text"
              accessibilityLabel={startTime.format('MMM')}
              testID="APPT_DATE_MONTH"
            >
              {startTime.format('MMM')}
            </Typography>
          </DateContainer>
          <AppointmentDetailContainer>
            <Time startTime={startTime} endTime={endTime} child={appointment.child} />
            <Location location={appointment?.encounter_location} />
            <Practitioner practitioner={practitioner} />
            {episode?.healthIssueTypeId && (
              <ReasonForConsult issueTypeId={episode?.healthIssueTypeId} />
            )}
            {showButtons && (
              <ManageButtons
                appointment={appointment}
                onCancelAppointment={onCancelAppointment}
                onRescheduleAppointment={onRescheduleAppointment}
                isCancelRequestRunning={isCancelRequestRunning}
                isRescheduleRequestRunning={isRescheduleRequestRunning}
              />
            )}
            {showQuickActions && <QuickActions appointment={appointment} />}
          </AppointmentDetailContainer>
        </AppointmentContainer>
      </WrapperComponent>

      <ConfirmationBottomSheet
        isVisible={!!sheetConfig}
        onClose={() => setSheetConfig(null)}
        config={sheetConfig}
      />
    </>
  )
}

export default AppointmentCard
